import { useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  Modal,
  ModalContent,
  ModalOverlay,
  ModalFooter,
} from '@chakra-ui/react';
import { Text, Button, Icon } from '@mercator-gcp/grout';
import { Link } from 'react-router-dom';
import { FlexRow } from 'components/layout/FlexRow';
import { useProjectTitles } from 'hooks/useProjectTitles';
import { trackProjectListClick } from 'utils/analytics';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { titleCase } from 'utils/misc';

const contentStyle = {
  maxWidth: '800px',
  width: 'fit-content',
  minWidth: '300px',
};

const modalBodyStyle = {
  padding: 'padding_16',
};

const linkStyle = {
  justifyContent: 'flex-start',
  gap: 'padding_4',
};

const listStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'padding_12',
};

const titleStyle = {
  paddingBottom: 'padding_16',
};

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: 'padding_16',
};

type Props = {
  projectSlugs: string[];
  documentTypeCode: string;
  documentId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const MultipleProjectLinksModal = ({
  projectSlugs,
  documentTypeCode,
  documentId,
  isOpen,
  onClose,
}: Props) => {
  const { projects } = useProjectTitles({ slugs: projectSlugs });

  const projectTitles = useMemo(() => {
    const titleAndSlugPairs = projects?.data?.map((proj) => {
      const modalQueryParameters = `document-type=${documentTypeCode}&document-id=${documentId}`;
      const projectURL = `/projects/${proj.slug}?${modalQueryParameters}`;

      return {
        title: titleCase(`${proj.address}, ${proj.city}`),
        projectSlug: proj.slug,
        projectURL,
      };
    });

    return titleAndSlugPairs;
  }, [projects]);

  const handleLinkClick = (projectSlug: string, linkText: string) => {
    trackProjectListClick({
      projectId: projectSlug,
      clickText: linkText,
      groupType: ProjectGroupTypes.UNCATEGORIZED,
      groupId: String(0),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent sx={contentStyle}>
        <Box sx={modalBodyStyle}>
          <Text.Lead sx={titleStyle}>Projects</Text.Lead>

          <List sx={listStyle}>
            {projectTitles?.map((projectInfo) => (
              <ListItem
                key={`multi-project-modal-box-to-project-slug-${projectInfo.projectSlug}`}
              >
                <Link
                  className="textLink"
                  to={projectInfo.projectURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    handleLinkClick(projectInfo.projectSlug, projectInfo.title)
                  }
                >
                  <FlexRow sx={linkStyle}>
                    <Text.BodyBold>{projectInfo.title}</Text.BodyBold>
                    <Icon name={Icon.Name.ChevronRight} size={16} />
                  </FlexRow>
                </Link>
              </ListItem>
            ))}
          </List>
        </Box>
        <ModalFooter sx={modalFooterStyle}>
          <Button variant="functional" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
